import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/button";
import PageContainer from "../../components/page-container";
import CustomDropdown from "../../components/custom-dropdown";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";
import { Loading } from "../loading";

const AddCourseTimes = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const {
    currentFile,
    formData,
    currentCourseIndex,
    isLastCourse,
    allCourseData,
  } = state || {};

  // Time options for dropdowns
  const timeOptions = [
    "7:00 AM",
    "7:15 AM",
    "7:30 AM",
    "7:45 AM",
    "8:00 AM",
    "8:15 AM",
    "8:30 AM",
    "8:45 AM",
    "9:00 AM",
    "9:15 AM",
    "9:30 AM",
    "9:45 AM",
    "10:00 AM",
    "10:15 AM",
    "10:30 AM",
    "10:45 AM",
    "11:00 AM",
    "11:15 AM",
    "11:30 AM",
    "11:45 AM",
    "12:00 PM",
    "12:15 PM",
    "12:30 PM",
    "12:45 PM",
    "1:00 PM",
    "1:15 PM",
    "1:30 PM",
    "1:45 PM",
    "2:00 PM",
    "2:15 PM",
    "2:30 PM",
    "2:45 PM",
    "3:00 PM",
    "3:15 PM",
    "3:30 PM",
    "3:45 PM",
    "4:00 PM",
    "4:15 PM",
    "4:30 PM",
    "4:45 PM",
    "5:00 PM",
    "5:15 PM",
    "5:30 PM",
    "5:45 PM",
    "6:00 PM",
    "6:15 PM",
    "6:30 PM",
    "6:45 PM",
    "7:00 PM",
    "7:15 PM",
    "7:30 PM",
    "7:45 PM",
    "8:00 PM",
    "8:15 PM",
    "8:30 PM",
    "8:45 PM",
    "9:00 PM",
    "9:15 PM",
    "9:30 PM",
    "9:45 PM",
    "10:00 PM",
  ];

  // State for single time range that applies to all selected days
  const [courseTime, setCourseTime] = useState({
    from: "",
    to: "",
  });
  // State for tracking validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!state) {
      navigate("/choose-sections");
      return;
    }
  }, [state, navigate]);

  const handleTimeChange = (timeType, value) => {
    setCourseTime((prev) => ({
      ...prev,
      [timeType]: value,
    }));

    // Clear errors when user makes a selection
    if (value && errors.time) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.time;
        return newErrors;
      });
    }
  };

  const handleSaveAndContinue = () => {
    // Validate that all times are selected
    const newErrors = {};

    if (!courseTime.from || !courseTime.to) {
      newErrors.time = "Please select both From and To times";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Save course data with single time range for all days (new format)
    const completeFormData = {
      ...formData,
      courseTime: courseTime, // Single time range
    };

    // Save to localStorage for later use in select-sections
    const existingUserData = localStorage.getItem("userCourseData");
    let userCourseData = {};

    if (existingUserData) {
      try {
        userCourseData = JSON.parse(existingUserData);
      } catch (error) {
        console.error("Error parsing existing user course data:", error);
      }
    }

    userCourseData[currentFile] = completeFormData;
    localStorage.setItem("userCourseData", JSON.stringify(userCourseData));
    sessionStorage.setItem("userCourseData", JSON.stringify(userCourseData)); // Backup for OAuth redirects

    // Navigate based on whether this is the last course
    if (isLastCourse) {
      navigate("/select-sections");
    } else {
      // Navigate back to Step 1 for next course
      navigate("/choose-sections", {
        state: {
          nextCourseIndex: currentCourseIndex + 1,
          allCourseData: {
            ...allCourseData,
            [currentFile]: completeFormData,
          },
        },
      });
    }
  };

  const handleBackToStep1 = () => {
    navigate("/choose-sections");
  };

  if (!formData) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <PageHeader
        title="Add course title and section"
        description={`Course title and/or section for ${
          currentFile?.replace("uploads/", "") || "SYLLABUS X.PDF"
        }`}
        step="Step 2 of 2"
      />

      <div className={s.courseTimesForm}>
        <div className={s.dayTimeRow}>
          <label className={s.dayLabel} htmlFor="time-dropdown">
            Course time (applies to all selected days: {formData?.selectedDays?.join(", ")})
          </label>

          <div className={`${s.timeSelectors} ${errors.time ? s.error : ""}`}>
            <CustomDropdown
              options={timeOptions}
              value={courseTime.from}
              onChange={(value) => handleTimeChange("from", value)}
              placeholder="From"
              hasError={!!errors.time}
            />
            <CustomDropdown
              options={timeOptions}
              value={courseTime.to}
              onChange={(value) => handleTimeChange("to", value)}
              placeholder="To"
              hasError={!!errors.time}
            />
          </div>

          {errors.time && <span className={s.errorText}>{errors.time}</span>}
        </div>
      </div>

      <div className={s.formActions}>
        <Button
          variant="primary"
          onClick={handleSaveAndContinue}
          className={s.saveButton}
        >
          Save and continue
        </Button>

        <Button
          variant="secondary"
          onClick={handleBackToStep1}
          className={s.backButton}
        >
          Back
        </Button>
      </div>
    </PageContainer>
  );
};

export default AddCourseTimes;
